import { ChangeDetectionStrategy, Component, OnInit, computed, signal } from '@angular/core';
import { AnalyticsDb } from 'lib/cosmos/analytics/analyticsDb.js';
import { CosmosDb } from 'lib/cosmos/cosmosDb.js';
import { IMultiSeries, ISingleSeries } from 'lib/ui/graphs/index.js';
import { IBaseTableItem, ITable } from 'lib/ui/table/index.js';
import { map } from 'rxjs';
import { TypeCoreComponent } from 'src/app/pages/type-routes/type-core.component.js';

type ExtendedSingleSeries = ISingleSeries & IBaseTableItem;

@Component({
    selector: 'mod-view-timezone-analytics',
    templateUrl: './view-timezone-analytics.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ViewTimezoneAnalyticsComponent extends TypeCoreComponent<void> implements OnInit {
	private readonly responseLinkedItems = signal<CosmosDb.ICosmosItem[]>([]);
	protected readonly dayTimezoneVisits = signal<AnalyticsDb.IAnalyticsItem[] | undefined>(undefined);

	public readonly dayTimezoneVisitSeries = computed<IMultiSeries[] | undefined>(() => {
		const dayVisits = this.dayTimezoneVisits();

		if (!dayVisits) {
			return undefined;
		}

		const multiSeries: IMultiSeries[] = [];

		for (const dayVisit of dayVisits) {
			if (!dayVisit.timezone) {
				continue;
			}

			const multiSeriesName: string = `${dayVisit.timezone} (all)`;

			let timezoneMultiSeriesAll: IMultiSeries | undefined = multiSeries.find((m) => m.name === multiSeriesName);

			if (!timezoneMultiSeriesAll) {
				timezoneMultiSeriesAll = {
					name: multiSeriesName,
					series: []
				};
				multiSeries.push(timezoneMultiSeriesAll);
			}

			timezoneMultiSeriesAll.series.push({
				name: this.dependencies.timeService.formatDayMonthAndYear(dayVisit.analyticsDate),
				value: dayVisit.eventCount.allCount
			});
		}

		return multiSeries;
	});

	public readonly timezonePieSeries = computed<ISingleSeries[] | undefined>(() => {
		const dayVisits = this.dayTimezoneVisits();

		if (!dayVisits) {
			return undefined;
		}

		const series: ISingleSeries[] = [];

		for (const dayVisit of dayVisits) {
			let existingSeries = series.find((m) => m.name === dayVisit.timezone);

			if (!existingSeries) {
				existingSeries = { name: dayVisit.timezone ?? 'n/a', value: 0 };
				series.push(existingSeries);
			}

			existingSeries.value += dayVisit.eventCount.allCount;
		}

		return series;
	});

	public readonly visitsPerTimezoneTable = computed<ITable<ExtendedSingleSeries> | undefined>(() => {
		const timezonePieSeries = this.timezonePieSeries();

		if (!timezonePieSeries) {
			return undefined;
		}

		const table: ITable<ExtendedSingleSeries> = {
			itemType: 'Analytics',
			search: {
				type: 'local'
			},

			noItemsText: {
				noItems: `There are no timezones`,
				noFilteredItems: `No timezones matches your filter`
			},
			items: timezonePieSeries.map((m) => {
				const extendedSeries: ExtendedSingleSeries = {
					id: m.name,
					name: m.name,
					value: m.value
				};

				return extendedSeries;
			}),
			id: (item) => item.id,
			title: {
				title: 'Timezone',
				value: (item) => item.name
			},
			fields: [
				{
					format: 'tag',
					title: `Visits count`,
					value: (item) => item.value,
					sortBy: (item) => item.value,
					isDefaultSort: true,
					defaultSortOrder: 'desc',
					showOnMobiles: true
				}
			]
		};

		return table;
	});

	constructor() {
		super();

		this.dataInit = () => {
			this.initDayTimezoneVisits();
		};
	}

	private initDayTimezoneVisits(): void {
		super.subscribeToObservable(
			this.httpService
				.get<AnalyticsDb.IAnalyticsItem[]>((builder) =>
					builder.withCustomApiRoute('analytics', 'getDayTimezoneVisits').build()
				)
				.pipe(
					map((response) => {
						this.dayTimezoneVisits.set(response.data);
						this.responseLinkedItems.update((linkedItems) => {
							return [...linkedItems, ...(response.linkedItems ?? [])];
						});
					})
				)
		);
	}
}
